import { Tooltip, TooltipProps } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

interface UITooltipProps extends Omit<TooltipProps, "slotProps"> {
  /**
   * Custom styles for the tooltip content
   */
  sx?: SxProps<Theme>;
  /**
   * Override default slot props if needed
   */
  slotProps?: TooltipProps["slotProps"];
}

/**
 * UITooltip component with consistent styling theme
 *
 * Features:
 * - White background with subtle shadow
 * - Consistent padding and typography
 * - Pre-line whitespace handling for multi-line content
 * - Styled arrow with shadow effects
 * - Responsive sizing with max dimensions
 */
export const UITooltip = ({
  children,
  sx,
  slotProps,
  ...props
}: UITooltipProps) => {
  return (
    <Tooltip
      arrow
      slotProps={{
        popper: {
          sx: { zIndex: 99999 },
          ...slotProps?.popper,
        },
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            borderRadius: 2,
            color: "#343c4391",
            padding: "15px 19px 18px 20px",
            maxWidth: "400px",
            width: "fit-content",
            maxHeight: "170px",
            fontSize: 12,
            lineHeight: 1.33,
            fontFamily: "Roboto",
            whiteSpace: "pre-line",
            "& .MuiTooltip-arrow": {
              color: "white",
              "&::before": {
                boxShadow:
                  "0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)",
              },
            },
            boxShadow:
              "0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)",
            ...sx,
          },
          ...slotProps?.tooltip,
        },
        ...slotProps,
      }}
      {...props}
    >
      {children}
    </Tooltip>
  );
};
